<template>
  <v-row row wrap>
    <v-col xs="12">
      <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
      <div class="d-md-none vehicles-mobile-list mt-2 px-2 pb-4">
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="red"
          class="mb-2"
        />

        <div
          v-for="(item, index) in cars"
          :key="item._id"
          class="vehicles-mobile-card"
          :class="{
            'vehicles-mobile-card--selected': index === activeRow,
          }"
          @click="activeRow = activeRow === index ? null : index"
        >
          <v-chip
            class="vehicles-mobile-card__date"
            size="x-small"
            variant="outlined"
            color="black"
          >
            {{ formatDate(item.date_created) }}
          </v-chip>

          <div class="vehicles-mobile-card__main">
            <v-btn
              icon
              variant="text"
              size="small"
              class="flex-shrink-0 mt-1"
              :loading="loadingSelect[index.toString()]"
              @click.stop="selectVehicle(item, index)"
            >
              <v-icon v-if="item.selected" color="grey-darken-2">
                mdi-checkbox-marked-outline
              </v-icon>
              <v-icon v-else color="grey-darken-2">
                mdi-checkbox-blank-outline
              </v-icon>
            </v-btn>

            <div class="vehicles-mobile-card__media">
              <button
                type="button"
                class="vehicles-mobile-card__image-btn"
                @click.stop="showImages(item.photos)"
              >
                <img
                  :src="
                    item.photos && item.photos.length
                      ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.photos[0].thumb}`
                      : `https://portal.lekotech.com/logo_small.png`
                  "
                  class="vehicles-mobile-card__img"
                />
              </button>
              <button
                type="button"
                class="vehicles-mobile-card__qr-btn"
                @click.stop="
                  openQrDialog({
                    id: item._id,
                    type: 'cars',
                    vehicle: item,
                    printed: item.printed,
                  })
                "
              >
                {{ item._id.slice(item._id.length - 6, item._id.length) }}
              </button>
            </div>

            <div class="vehicles-mobile-card__info">
              <div class="vehicles-mobile-card__info-top">
                 <v-chip
                v-if="
                  ($store.getters.role === 'LekoAdmin' ||
                    $store.getters.role === 'SuperAdmin') &&
                  item.institution_id?.title
                "
                   class=" text-xs"
                 size="x-small"
              >
                {{ item.institution_id.title }}
              </v-chip>
                <div class="font-weight-bold text-primary text-body-2">
                  {{ item.brand.title }}
                </div>
                <div class="text-body-2">
                  {{ item.model.description }}
                </div>
                <div class="text-caption">
                  {{ item.version.description }}
                </div>
              </div>

              <div class="text-caption mt-1">
                {{ formatMonthYear(item.version.startingDate) }} -
                {{ formatMonthYear(item.version.endingDate) }}
              </div>
              <div class="text-caption mt-1">
                <span v-if="item.registrationYear">
                  {{ $t("vehicle_entry.datatable.registration_year") }}:
                  <strong>{{ item.registrationYear }}</strong>
                </span>
                <span v-if="item.km != null" class="ml-2">
                  {{ $t("vehicle_entry.datatable.number_of_km") }}:
                  <strong>{{ item.km }}</strong>
                </span>
              </div>
              <div v-if="item.pra" class="text-caption mt-1">
                {{ $t("vehicle_entry.datatable.practice_number") }}:
                <strong>{{ item.pra }}</strong>
              </div>
              <div class="text-caption mt-1">
                <span v-if="item.version.numberOfDoors">
                  {{ item.version.numberOfDoors }}
                  {{ $t("vehicle_entry.datatable.doors") }}
                </span>
                <span
                  v-if="
                    item.version &&
                    item.version.power &&
                    item.version.power.kw
                  "
                  class="ml-2"
                >
                  {{ item.version.power.kw }} kw
                </span>
              </div>
              <div
                v-if="item.version.engineCode || item.version.supplyCode"
                class="text-caption mt-1"
              >
                <span v-if="item.version.engineCode">
                  {{ $t("vehicle_entry.datatable.engine") }}:
                  {{ item.version.engineCode }}
                </span>
                <span v-if="item.version.supplyCode" class="ml-2">
                  {{ getSupplyLocale(item.version.supplyCode) }}
                </span>
              </div>
              <div
                v-if="item.transmissionType || item.transmissionLayout"
                class="text-caption mt-1"
              >
                {{ item.transmissionType }}
                <span v-if="item.transmissionLayout">
                  · {{ item.transmissionLayout }}
                </span>
              </div>
              <div
                v-if="item.location && item.location.name"
                class="text-caption text-grey-darken-1 mt-1"
              >
                {{ item.location.name }}
              </div>
              <v-chip
                v-if="item.video"
                size="x-small"
                color="grey-darken-1"
                variant="tonal"
                class="mt-1 pointer"
                @click.stop="showVideo(item.video)"
              >
                <v-icon start size="12">mdi-video-outline</v-icon>
                {{ $t("vehicle_entry.datatable.video") }}
              </v-chip>
            </div>
          </div>

          <div class="vehicles-mobile-card__actions">
            <template v-if="complianceEnabled">
              <compliance-summary-pill
                :car="item"
                :summary="mobileSummary(item)"
                :failed="acceptanceFailed"
              />
              <v-btn
                icon
                size="small"
                color="primary"
                :variant="acceptanceByCar[item._id] ? 'tonal' : 'text'"
                :title="acceptanceActionLabel(item)"
                :loading="acceptanceStarting[item._id] === true"
                @click.stop="openAcceptance(item)"
              >
                <v-icon size="20">mdi-clipboard-check-outline</v-icon>
              </v-btn>
            </template>
            <span
              v-if="$store.getters.role === 'SuperAdmin'"
              class="infocar-parts d-inline-flex align-center mx-2"
              :title="
                item.hasInfocarParts
                  ? $t('vehicle_entry.datatable.has_infocar_parts')
                  : $t('vehicle_entry.datatable.no_infocar_parts')
              "
            >
              <v-icon
                size="20"
                :color="item.hasInfocarParts ? 'primary' : 'grey'"
              >
                mdi-information
              </v-icon>
              <v-icon
                v-if="item.hasInfocarParts"
                class="infocar-parts__check"
                color="green"
                size="12"
              >
                mdi-check-circle
              </v-icon>
            </span>
            <v-btn icon variant="text" size="small" @click.stop="share(item)">
              <v-icon size="20" color="grey-darken-2">
                mdi-share-variant-outline
              </v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              @click.stop="viewDetails(item)"
            >
              <v-icon size="20" color="grey-darken-2">mdi-view-list</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              @click.stop="editVehicle(item)"
            >
              <v-icon size="20" color="grey-darken-2">mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              @click.stop="openQrGenerationDialog(item)"
            >
              <v-icon size="20" color="grey-darken-2">mdi-qrcode</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              :loading="loadingArchive[index.toString()]"
              @click.stop="exportArchive(item, index)"
            >
              <v-icon size="20" color="grey-darken-2">
                mdi-file-download-outline
              </v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              @click.stop="openInfoCarCad(item)"
            >
              <v-icon size="20" color="grey-darken-2">mdi-car</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              @click.stop="openTransferDialog(item)"
            >
              <v-icon size="20" color="grey-darken-2">
                mdi-swap-horizontal
              </v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              :loading="loadingRemove[index.toString()]"
              @click.stop="openConfirmRemoveModal(item, index)"
            >
              <v-icon size="20" color="grey-darken-2">mdi-close</v-icon>
            </v-btn>
            <v-btn
              v-if="
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'SuperAdmin'
              "
              icon
              variant="text"
              size="small"
              :loading="loadingDelete[index.toString()]"
              :disabled="
                (!!item.shipment && !!item.shipment.id) ||
                (item.shipment &&
                  item.shipment.entries &&
                  item.shipment.entries.length > 0)
              "
              @click.stop="openConfirmDeleteModal(item, index)"
            >
              <v-icon size="20" color="error">mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>

        <v-alert
          v-if="!loading && cars.length < 1"
          type="info"
          variant="tonal"
          class="mt-2"
        >
          No data available.
        </v-alert>
      </div>
      <!-- ===================================================================== -->

      <div class="vehicles-table-host pb-2">
        <StickyHScroll>
          <v-data-table-server
          v-model:options="pagination"
          :headers="tableHeaders"
          :items="cars"
          :page="pagination.page"
          :items-length="total"
          :loading="loading"
          class="elevation-1 nowrap-headers pb-8"
          :items-per-page="pagination.itemsPerPage"
          :items-per-page-options="[
            { value: 5, title: '5' },
            { value: 10, title: '10' },
            { value: 25, title: '25' },
            { value: 50, title: '50' },
          ]"
          items-per-page-text="Rows per page"
          @update:options="updatePagination"
        >
          <template #progress>
            <v-progress-linear color="red" indeterminate />
          </template>

          <template #item="{ item, index }">
            <tr
              :class="{
                tableRowColorSelected: index === activeRow,
              }"
              @click="activeRow = activeRow === index ? null : index"
            >
              <td class="justify-center">
                <v-row class="pointer ma-1 text-center justify-center">
                  <v-btn
                    icon
                    variant="text"
                    :loading="loadingSelect[index.toString()]"
                    @click="selectVehicle(item, index)"
                  >
                    <v-icon v-if="item.selected" color="grey-darken-2">
                      mdi-checkbox-marked-outline
                    </v-icon>
                    <v-icon v-else color="grey-darken-2">
                      mdi-checkbox-blank-outline
                    </v-icon>
                  </v-btn>
                </v-row>
              </td>
              <td class="ma-1 pt-1 pb-1">
                <v-row
                  class="pointer ma-1 text-center justify-center d-block"
                  @click="
                    openQrDialog({
                      id: item._id,
                      type: 'cars',
                      vehicle: item,
                      printed: item.printed,
                    })
                  "
                >
                  <qr-code :size="60" :text="item._id" />
                  <h4>
                    {{ item._id.slice(item._id.length - 6, item._id.length) }}
                  </h4>
                </v-row>
              </td>
              <!----------------------------------------ACTIONS---------------------------------------->
              <td>
                <v-row class="d-flex flex-nowrap">
                  <!--SHARE-->
                  <v-btn icon variant="text" @click="share(item)">
                    <v-icon color="grey-darken-2"
                      >mdi-share-variant-outline
                    </v-icon>
                  </v-btn>

                  <v-btn icon variant="text" @click="viewDetails(item)">
                    <v-icon color="grey-darken-2"> mdi-view-list </v-icon>
                  </v-btn>

                  <v-btn icon variant="text" @click="editVehicle(item)">
                    <v-icon color="grey-darken-2"> mdi-pencil </v-icon>
                  </v-btn>

                  <v-btn
                    icon
                    variant="text"
                    @click="openQrGenerationDialog(item)"
                  >
                    <v-icon color="grey-darken-2"> mdi-qrcode </v-icon>
                  </v-btn>

                  <v-btn
                    icon
                    variant="text"
                    :loading="loadingArchive[index.toString()]"
                    @click="exportArchive(item, index)"
                  >
                    <v-icon color="grey-darken-2">
                      mdi-file-download-outline
                    </v-icon>
                  </v-btn>

                  <v-btn
                    v-if="complianceEnabled"
                    icon
                    color="primary"
                    :variant="acceptanceByCar[item._id] ? 'tonal' : 'text'"
                    :title="acceptanceActionLabel(item)"
                    :loading="acceptanceStarting[item._id] === true"
                    @click="openAcceptance(item)"
                  >
                    <v-icon> mdi-clipboard-check-outline </v-icon>
                  </v-btn>
                </v-row>
                <v-row>
                  <span
                    v-if="$store.getters.role === 'SuperAdmin'"
                    class="infocar-parts d-inline-flex align-center mr-2"
                    :title="
                      item.hasInfocarParts
                        ? $t('vehicle_entry.datatable.has_infocar_parts')
                        : $t('vehicle_entry.datatable.no_infocar_parts')
                    "
                  >
                    <v-icon :color="item.hasInfocarParts ? 'primary' : 'grey'">
                      mdi-information
                    </v-icon>
                    <v-icon
                      v-if="item.hasInfocarParts"
                      class="infocar-parts__check"
                      color="green"
                      size="14"
                    >
                      mdi-check-circle
                    </v-icon>
                  </span>

                  <v-btn icon variant="text" @click="openInfoCarCad(item)">
                    <v-icon color="grey-darken-2"> mdi-car </v-icon>
                  </v-btn>

                  <v-btn icon variant="text" @click="openTransferDialog(item)">
                    <v-icon color="grey-darken-2"> mdi-swap-horizontal </v-icon>
                  </v-btn>

                  <v-btn
                    icon
                    variant="text"
                    :loading="loadingRemove[index.toString()]"
                    @click="openConfirmRemoveModal(item, index)"
                  >
                    <v-icon color="grey-darken-2"> mdi-close </v-icon>
                  </v-btn>

                  <v-btn
                    v-if="
                      $store.getters.role === 'LekoAdmin' ||
                      $store.getters.role === 'SuperAdmin'
                    "
                    icon
                    variant="text"
                    :loading="loadingDelete[index.toString()]"
                    :disabled="
                      (!!item.shipment && !!item.shipment.id) ||
                      (item.shipment &&
                        item.shipment.entries &&
                        item.shipment.entries.length > 0)
                    "
                    @click="openConfirmDeleteModal(item, index)"
                  >
                    <v-icon color="grey-darken-2"> mdi-delete </v-icon>
                  </v-btn>
                </v-row>
              </td>
              <!----------------------------------------IMAGES----------------------------------------->
              <td>
                <img
                  :src="
                    item.photos && item.photos.length
                      ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.photos[0].thumb}`
                      : `https://portal.lekotech.com/logo_small.png`
                  "
                  class="ma-1 pointer"
                  height="75"
                  width="100"
                  @click="showImages(item.photos)"
                />
              </td>
              <td>
                <video
                  v-if="item.video"
                  :src="`https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.video}`"
                  class="ma-1 pointer"
                  height="75"
                  width="100"
                  @click="showVideo(item.video)"
                />
              </td>
              <td
                v-if="
                  $store.getters.role === 'LekoAdmin' ||
                  $store.getters.role === 'SuperAdmin'
                "
              >
                {{ item.institution_id ? item.institution_id.title : "" }}
              </td>
              <td class="pl-2">{{ item.brand.title }}</td>
              <td>{{ item.model.description }}</td>
              <td>{{ item.version.description }}</td>
              <td>
                <div class="text-truncate">
                  {{ formatMonthYear(item.version.startingDate) }} -
                  {{ formatMonthYear(item.version.endingDate) }}
                </div>
              </td>
              <td>{{ item.registrationYear }}</td>
              <td>{{ item.km }}</td>
              <td>{{ item.pra }}</td>
              <td>
                {{ item.version.numberOfDoors }}
                {{ $t("vehicle_entry.datatable.doors") }}
              </td>
              <td>
                {{
                  item.version && item.version.power && item.version.power.kw
                    ? item.version.power.kw
                    : ""
                }}
                kw
              </td>
              <td>{{ item.version.engineCode }}</td>
              <td>{{ getSupplyLocale(item.version.supplyCode) }}</td>
              <td>{{ item.transmissionType }}</td>
              <td>{{ item.transmissionLayout }}</td>
              <td>
                {{
                  item.location && item.location.name ? item.location.name : ""
                }}
              </td>
              <td>
                <v-btn variant="outlined" small color="black">
                  {{ formatDate(item.date_created) }}
                </v-btn>
              </td>
              <td v-if="complianceEnabled">
                <span
                  v-if="acceptanceFailed || !acceptanceLoaded"
                  :title="
                    acceptanceFailed ? $t('acceptance.list.load_failed') : ''
                  "
                >
                  —
                </span>
                <v-chip
                  v-else
                  size="small"
                  variant="tonal"
                  :color="acceptanceColor(item)"
                >
                  {{ $t(`acceptance.status.${acceptanceStatus(item)}`) }}
                </v-chip>
              </td>
              <!-- With the workshop on, the Bonifica column shows this due date. -->
              <td v-if="complianceEnabled && !workshopEnabled">
                {{ deadlineText(item, "depollution") || "—" }}
              </td>
              <td v-if="complianceEnabled">
                {{ deadlineText(item, "deregistrationRequest") || "—" }}
              </td>
              <td v-if="praEnabled">
                <span
                  v-if="acceptanceFailed || !acceptanceLoaded"
                  :title="
                    acceptanceFailed ? $t('acceptance.list.load_failed') : ''
                  "
                >
                  —
                </span>
                <span v-else-if="!praChips(item).length">—</span>
                <span v-else class="d-inline-flex flex-nowrap">
                  <v-chip
                    v-for="chip in praChips(item)"
                    :key="chip.key"
                    size="small"
                    variant="tonal"
                    class="mr-1"
                    :color="chip.color"
                  >
                    {{ $t(chip.key) }}
                  </v-chip>
                </span>
              </td>
              <td v-if="workshopEnabled">
                <span
                  v-if="acceptanceFailed || !acceptanceLoaded"
                  :title="
                    acceptanceFailed ? $t('acceptance.list.load_failed') : ''
                  "
                >
                  —
                </span>
                <template v-else>
                  <v-chip
                    v-if="workshopCell(item).chip"
                    size="small"
                    variant="tonal"
                    class="text-no-wrap"
                    :color="workshopCell(item).chip.color"
                    :title="workshopTitle(workshopCell(item))"
                  >
                    {{ workshopText(workshopCell(item)) }}
                  </v-chip>
                  <span v-else>—</span>
                  <v-chip
                    v-if="releasedChipOf(item)"
                    size="small"
                    variant="tonal"
                    class="text-no-wrap ml-1"
                    :color="releasedChipOf(item).color"
                  >
                    {{ $t(releasedChipOf(item).key) }}
                  </v-chip>
                  <div
                    v-if="workshopCell(item).dueDate"
                    class="text-caption text-no-wrap"
                  >
                    {{
                      $t("acceptance.depollution.list.due_on", {
                        date: workshopCell(item).dueDate,
                      })
                    }}
                  </div>
                </template>
              </td>
            </tr>
            <tr v-if="cars && cars.length < 1">
              <td :colspan="headers.length" class="text-center pa-4">
                <v-alert type="info" border="left" color="red lighten-2" text>
                  No data available.
                </v-alert>
              </td>
            </tr>
          </template>
        </v-data-table-server>
        </StickyHScroll>
      </div>

      <v-dialog
        v-model="confirmRemoveModal"
        hide-overlay
        persistent
        width="500"
      >
        <v-card>
          <v-card-text class="pt-4 pb-0 mb-0">
            <v-alert
              :value="true"
              type="error"
              border="bottom"
              variant="outlined"
              text
            >
              {{ $t("vehicle_entry.datatable.confirmRemoveAlert") }}
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              variant="outlined"
              color="red-darken-1"
              @click="confirmRemoveModal = false"
            >
              {{ $t("vehicle_entry.info.no") }}
            </v-btn>
            <v-btn
              variant="outlined"
              color="blue darken-1"
              @click="removeVehicle"
            >
              {{ $t("vehicle_entry.info.yes") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="removeSuccessModal" hide-overlay width="500">
        <v-card>
          <v-card-text class="pt-4 pb-0 mb-0">
            <v-alert type="success" border="bottom" variant="outlined">
              {{ $t("vehicle_entry.datatable.removeSuccessAlert") }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              variant="outlined"
              color="success"
              @click="removeSuccessModal = false"
            >
              {{ $t("vehicle_entry.close") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="confirmDeleteModal"
        hide-overlay
        persistent
        width="500"
      >
        <v-card>
          <v-card-text>
            {{ $t("vehicle_entry.datatable.confirmDeleteAlert") }}
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              variant="outlined"
              color="red-darken-1"
              @click="confirmDeleteModal = false"
            >
              {{ $t("vehicle_entry.info.no") }}
            </v-btn>
            <v-btn
              variant="outlined"
              color="blue darken-1"
              @click="deleteVehicle"
            >
              {{ $t("vehicle_entry.info.yes") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar
        v-if="complianceEnabled"
        v-model="acceptanceSnackbar"
        color="error"
        location="top"
      >
        {{ $t("acceptance.list.start_failed") }}
      </v-snackbar>
    </v-col>
  </v-row>
</template>

<script>
import moment from "moment";
import axios from "axios";
import VueQrCode from "vue-qr-generator";

import getRequest from "../../api/getRequest";
import putRequest from "../../api/putRequest";
import deleteRequest from "../../api/deleteRequest";
import {
  newCommandId,
  startDossier,
  getDossiersByCars,
} from "@/api/compliance";
import { dueDateText } from "@/components/Acceptance/deadlines.mjs";
import { listChips } from "@/components/Acceptance/praView.mjs";
import { listCell } from "@/components/Workshop/workshopView.mjs";
import { releasedChip } from "@/components/Waste/carBodyRelease.mjs";
import { complianceSummary } from "@/components/VehicleEntry/complianceSummary.mjs";
import ComplianceSummaryPill from "@/components/VehicleEntry/complianceSummaryPill.vue";

let lastRequestId = 0;

export default {
  components: {
    "qr-code": VueQrCode,
    ComplianceSummaryPill,
  },
  filters: {
    date(date) {
      moment.locale(localStorage.getItem("language"));
      const valid = moment(date).isValid();
      if (valid) return moment(date).format("MM/YYYY");
      return "";
    },
    fullDate(date) {
      moment.locale(localStorage.getItem("language"));
      return moment(date).format("LLL");
    },
  },
  data: () => ({
    headers: [
      { title: "Selected", sortable: false, key: "" },
      { title: "ID", sortable: true, key: "_id" },
      { title: "Actions", sortable: false, key: "" },
      { title: "Photo", sortable: false, key: "" },
      { title: "Video", sortable: false, key: "" },
      { title: "Brand", sortable: true, key: "brand.brandCode" },
      { title: "Model", sortable: true, key: "model.modelCode" },
      { title: "Version", sortable: true, key: "version.versionCode" },
      { title: "Year", sortable: false, key: "" },
      { title: "Registration Year", sortable: false, key: "" },
      { title: "KM", sortable: false, key: "" },
      { title: "PRA", sortable: true, key: "pra" },
      { title: "Number of doors", sortable: false, key: "" },
      { title: "Power", sortable: false, key: "" },
      { title: "Engine", sortable: false, key: "" },
      { title: "Supply", sortable: false, key: "" },
      { title: "Transmission Type", sortable: false, key: "" },
      { title: "Transmission Layout", sortable: false, key: "" },
      { title: "Location", sortable: true, key: "location.name" },
      { title: "Date Created", sortable: true, key: "date_created" },
    ],
    headersAdmin: [
      { title: "Selected", sortable: false, key: "" },
      { title: "ID", sortable: true, key: "_id" },
      { title: "Actions", sortable: false, key: "" },
      { title: "Photo", sortable: false, key: "" },
      { title: "Video", sortable: false, key: "" },
      { title: "Institution", sortabe: true, key: "institution_id" },
      { title: "Brand", sortable: true, key: "brand.brandCode" },
      { title: "Model", sortable: true, key: "model.modelCode" },
      { title: "Version", sortable: true, key: "version.versionCode" },
      { title: "Year", sortable: false, key: "" },
      { title: "Registration Year", sortable: false, key: "" },
      { title: "KM", sortable: false, key: "" },
      { title: "PRA", sortable: true, key: "pra" },
      { title: "Number of doors", sortable: false, key: "" },
      { title: "Power", sortable: false, key: "" },
      { title: "Engine", sortable: false, key: "" },
      { title: "Supply", sortable: false, key: "" },
      { title: "Transmission Type", sortable: false, key: "" },
      { title: "Transmission Layout", sortable: false, key: "" },
      { title: "Location", sortable: true, key: "location.name" },
      { title: "Date Created", sortable: true, key: "date_created" },
    ],
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["date_created"],
      sortBy: [{ key: "date_created", order: "desc" }],
      sortDesc: [true],
    },
    filters: {
      type: null,
      search: null,
      location: null,
      part: null,
      brand: null,
      model: null,
      version: null,
      engine: null,
      km: [0, 1000000],
      startDate: null,
      endDate: null,
    },
    cars: [],
    total: 0,
    loading: false,
    loadingSelect: {},
    loadingRemove: {},
    loadingDelete: {},
    loadingArchive: {},
    confirmRemoveModal: false,
    removeSuccessModal: false,
    confirmDeleteModal: false,
    vehicle: {},
    index: "",
    activeRow: null,
    // Dismantling & Compliance (only used when complianceEnabled).
    acceptanceByCar: {}, // carId -> { carId, dossierId, status, deadlines, pra? }
    acceptanceLoaded: false,
    acceptanceFailed: false,
    acceptanceStarting: {}, // carId -> true while POST /compliance/dossiers runs
    acceptanceCommandIds: {}, // carId -> commandId, reused when the same start is retried
    acceptanceSnackbar: false,
  }),
  computed: {
    server() {
      return this.$serverUri;
    },
    complianceEnabled() {
      return this.$store.getters.complianceEnabled;
    },
    praEnabled() {
      return this.$store.getters.praEnabled;
    },
    workshopEnabled() {
      return this.$store.getters.workshopEnabled;
    },
    wasteEnabled() {
      return this.$store.getters.wasteEnabled === true;
    },
    tableHeaders() {
      const base =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.headersAdmin
          : this.headers;
      if (!this.complianceEnabled) return base;
      return [
        ...base,
        { title: this.$t("acceptance.list.column"), sortable: false, key: "" },
        // With the workshop on, the Bonifica column carries the depollution due date.
        ...(this.workshopEnabled
          ? []
          : [
              {
                title: this.$t("acceptance.list.depollution_column"),
                sortable: false,
                key: "acceptanceDepollution",
              },
            ]),
        {
          title: this.$t("acceptance.list.deregistration_column"),
          sortable: false,
          key: "acceptanceDeregistration",
        },
        ...(this.praEnabled
          ? [
              {
                title: this.$t("acceptance.pra.title"),
                sortable: false,
                key: "acceptancePra",
              },
            ]
          : []),
        ...(this.workshopEnabled
          ? [
              {
                title: this.$t("acceptance.depollution.column"),
                sortable: false,
                key: "acceptanceWorkshop",
              },
            ]
          : []),
      ];
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getCars();
      },
    },
    "$i18n.locale"() {
      this.getLocale();
    },
  },
  created() {
    this.getLocale();
    this.getCars();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("vehicle_entry.datatable.selected");
      this.headers[1].title = this.$t("vehicle_entry.datatable.id");
      this.headers[2].title = this.$t("vehicle_entry.datatable.actions");
      this.headers[3].title = this.$t("vehicle_entry.datatable.images");
      this.headers[4].title = this.$t("vehicle_entry.datatable.video");
      this.headers[5].title = this.$t("vehicle_entry.datatable.brand");
      this.headers[6].title = this.$t("vehicle_entry.datatable.model");
      this.headers[7].title = this.$t("vehicle_entry.datatable.version");
      this.headers[8].title = this.$t("vehicle_entry.datatable.year");
      this.headers[9].title = this.$t(
        "vehicle_entry.datatable.registration_year",
      );
      this.headers[10].title = this.$t("vehicle_entry.datatable.number_of_km");
      this.headers[11].title = this.$t(
        "vehicle_entry.datatable.practice_number",
      );
      this.headers[12].title = this.$t(
        "vehicle_entry.datatable.number_of_doors",
      );
      this.headers[13].title = this.$t("vehicle_entry.datatable.power");
      this.headers[14].title = this.$t("vehicle_entry.datatable.engine");
      this.headers[15].title = this.$t("vehicle_entry.datatable.supply");
      this.headers[16].title = this.$t(
        "vehicle_entry.datatable.transmission_type",
      );
      this.headers[17].title = this.$t(
        "vehicle_entry.datatable.transmission_layout",
      );
      this.headers[18].title = this.$t("vehicle_entry.datatable.location");
      this.headers[19].title = this.$t("vehicle_entry.datatable.date_created");
      this.headersAdmin[0].title = this.$t("vehicle_entry.datatable.selected");
      this.headersAdmin[1].title = this.$t("vehicle_entry.datatable.id");
      this.headersAdmin[2].title = this.$t("vehicle_entry.datatable.actions");
      this.headersAdmin[3].title = this.$t("vehicle_entry.datatable.images");
      this.headersAdmin[4].title = this.$t("vehicle_entry.datatable.video");
      this.headersAdmin[5].title = this.$t(
        "vehicle_entry.datatable.institution",
      );
      this.headersAdmin[6].title = this.$t("vehicle_entry.datatable.brand");
      this.headersAdmin[7].title = this.$t("vehicle_entry.datatable.model");
      this.headersAdmin[8].title = this.$t("vehicle_entry.datatable.version");
      this.headersAdmin[9].title = this.$t("vehicle_entry.datatable.year");
      this.headersAdmin[10].title = this.$t(
        "vehicle_entry.datatable.registration_year",
      );
      this.headersAdmin[11].title = this.$t(
        "vehicle_entry.datatable.number_of_km",
      );
      this.headersAdmin[12].title = this.$t(
        "vehicle_entry.datatable.practice_number",
      );
      this.headersAdmin[13].title = this.$t(
        "vehicle_entry.datatable.number_of_doors",
      );
      this.headersAdmin[14].title = this.$t("vehicle_entry.datatable.power");
      this.headersAdmin[15].title = this.$t("vehicle_entry.datatable.engine");
      this.headersAdmin[16].title = this.$t("vehicle_entry.datatable.supply");
      this.headersAdmin[17].title = this.$t(
        "vehicle_entry.datatable.transmission_type",
      );
      this.headersAdmin[18].title = this.$t(
        "vehicle_entry.datatable.transmission_layout",
      );
      this.headersAdmin[19].title = this.$t("vehicle_entry.datatable.location");
      this.headersAdmin[20].title = this.$t(
        "vehicle_entry.datatable.date_created",
      );
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
    getCars() {
      const requestId = ++lastRequestId;
      this.loading = true;
      // eslint-disable-next-line no-nested-ternary
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        "/car",
        `status=1&institution_id=${
          this.$store.getters.role !== "LekoAdmin" &&
          this.$store.getters.role !== "SuperAdmin"
            ? this.$store.getters.institutionId
            : this.filters.institution
              ? this.filters.institution
              : ""
        }&role=${this.$store.getters.role}&page=${this.pagination.page}&limit=${
          this.pagination.itemsPerPage
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&type=${this.filters.type ? this.filters.type : ""}&search=${this.filters.search ? this.filters.search : ""}&part=${
          this.filters.part ? this.filters.part : ""
        }&km=${this.filters.km}&brand=${this.filters.brand ? this.filters.brand : ""}&model=${
          this.filters.model ? this.filters.model : ""
        }&engine=${this.filters.engine ? this.filters.engine : ""}&version=${
          this.filters.version ? this.filters.version : ""
        }&startDate=${this.filters.startDate ? this.filters.startDate : ""}&endDate=${this.filters.endDate ? this.filters.endDate : ""}&location=${this.filters.location ? this.filters.location : ""}`,
      )
        .then((response) => {
          if (requestId !== lastRequestId) return;
          this.cars = response.cars;
          this.total = response.count;
          this.loading = false;
          this.$emit("cars", this.cars);
          this.loadAcceptance(requestId);
        })
        .catch(() => {
          this.loading = false;
        })
        .finally(() => {});
    },
    // Acceptance status for the cars on this page: one by-cars call, after the list loaded.
    // A failure leaves the list working and shows a dash with a tooltip.
    loadAcceptance(requestId) {
      if (!this.complianceEnabled) return;
      this.acceptanceLoaded = false;
      this.acceptanceFailed = false;
      this.acceptanceByCar = {};
      const carIds = (this.cars || []).map((car) => car._id).slice(0, 50);
      if (!carIds.length) {
        this.acceptanceLoaded = true;
        return;
      }
      getDossiersByCars(carIds)
        .then((response) => {
          if (requestId !== lastRequestId) return;
          const byCar = {};
          (response.dossiers || []).forEach((row) => {
            byCar[row.carId] = row;
          });
          this.acceptanceByCar = byCar;
          this.acceptanceLoaded = true;
        })
        .catch(() => {
          if (requestId !== lastRequestId) return;
          this.acceptanceFailed = true;
        });
    },
    acceptanceStatus(car) {
      const row = this.acceptanceByCar[car._id];
      return row ? row.status : "not_started";
    },
    // DD/MM/YYYY of one deadline of the car's dossier; '' until it is confirmed or while loading.
    deadlineText(car, kind) {
      if (!this.acceptanceLoaded || this.acceptanceFailed) return "";
      return dueDateText(this.acceptanceByCar[car._id], kind);
    },
    // "Pratica PRA" chips of the car's case; [] (shown as "—") while loading or without a case.
    praChips(car) {
      if (!this.acceptanceLoaded || this.acceptanceFailed) return [];
      const row = this.acceptanceByCar[car._id];
      return listChips(row && row.pra);
    },
    // "Bonifica" cell of the car's checklist: { chip, dueDate }; no chip (shown as "—") while
    // loading, without a checklist or for a draft.
    workshopCell(car) {
      if (!this.acceptanceLoaded || this.acceptanceFailed) {
        return { chip: null, dueDate: "" };
      }
      return listCell(this.acceptanceByCar[car._id]);
    },
    // The grey "Carcassa rilasciata" chip (Stage 6 slice 4): waste on and the body released.
    releasedChipOf(car) {
      if (
        !this.wasteEnabled ||
        !this.acceptanceLoaded ||
        this.acceptanceFailed
      ) {
        return null;
      }
      return releasedChip(this.acceptanceByCar[car._id]);
    },
    workshopText(cell) {
      return cell.chip ? this.$t(cell.chip.key, cell.chip.params) : "";
    },
    workshopTitle(cell) {
      const title = cell.chip && cell.chip.title;
      return title ? this.$t(title.key, title.params) : undefined;
    },
    // Mobile card "Compliance" pill and modal; null (shown as "—") while loading or after a failure.
    mobileSummary(car) {
      if (!this.acceptanceLoaded || this.acceptanceFailed) return null;
      return complianceSummary(this.acceptanceByCar[car._id], {
        praEnabled: this.praEnabled,
        workshopEnabled: this.workshopEnabled,
        wasteEnabled: this.wasteEnabled,
      });
    },
    acceptanceColor(car) {
      const status = this.acceptanceStatus(car);
      if (status === "confirmed") return "green";
      if (status === "draft") return "amber-darken-3";
      return "grey";
    },
    acceptanceActionLabel(car) {
      return this.acceptanceByCar[car._id]
        ? this.$t("acceptance.list.open")
        : this.$t("acceptance.list.start");
    },
    // Opens the dossier, starting it first when the car has none (or the status is unknown:
    // the API then returns the existing dossier).
    async openAcceptance(car) {
      const row = this.acceptanceByCar[car._id];
      if (row) {
        this.$router.push(`/acceptance/${row.dossierId}`);
        return;
      }
      if (this.acceptanceStarting[car._id]) return;
      if (!this.acceptanceCommandIds[car._id]) {
        this.acceptanceCommandIds[car._id] = newCommandId();
      }
      this.acceptanceStarting[car._id] = true;
      try {
        const result = await startDossier(
          car._id,
          this.acceptanceCommandIds[car._id],
        );
        this.$router.push(`/acceptance/${result.dossier.id}`);
      } catch (err) {
        this.acceptanceSnackbar = true;
      } finally {
        this.acceptanceStarting[car._id] = false;
      }
    },
    setFilters(filters) {
      this.filters = filters;
      this.getCars();
    },
    viewDetails(car) {
      this.$emit("view-details", car);
    },
    share(data) {
      this.$emit("share", data);
    },
    openQrGenerationDialog(data) {
      this.$emit("generate-qr", data);
    },
    selectVehicle(vehicle, index) {
      this.loadingSelect[index.toString()] = true;
      putRequest("/car/select", `id=${vehicle._id}`, {})
        .then((result) => {
          this.getCars();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingSelect[index.toString()] = false;
        });
    },
    editVehicle(vehicle) {
      this.$emit("edit-vehicle", vehicle);
    },
    openConfirmRemoveModal(vehicle, index) {
      this.confirmRemoveModal = true;
      this.vehicle = vehicle;
      this.index = index;
    },
    openConfirmDeleteModal(vehicle, index) {
      this.confirmDeleteModal = true;
      this.vehicle = vehicle;
      this.index = index;
    },
    removeVehicle() {
      this.loadingRemove[this.index.toString()] = true;
      deleteRequest("/car", `id=${this.vehicle._id}`)
        .then(() => {
          this.getCars();
          this.confirmRemoveModal = false;
          this.removeSuccessModal = true;
        })
        .catch(() => {
          this.confirmRemoveModal = false;
        })
        .finally(() => {
          this.loadingRemove[this.index.toString()] = false;
          this.$emit("removed");
        });
    },
    deleteVehicle() {
      this.confirmDeleteModal = true;
      this.loadingDelete[this.index.toString()] = true;
      deleteRequest("/car/delete", `id=${this.vehicle._id}`)
        .then((response) => {
          this.getCars();
        })
        .catch(() => {})
        .finally(() => {
          this.confirmDeleteModal = false;
          this.loadingDelete[this.index.toString()] = false;
          this.$emit("removed");
        });
    },
    showImages(images) {
      this.$emit("images", images);
    },
    showVideo(video) {
      this.$emit("video", video);
    },
    exportArchive(car, index) {
      const cars = [car._id];

      this.index = index;
      this.loadingArchive[this.index.toString()] = true;
      const { token } = localStorage;
      axios
        .post(
          `${this.$serverUri}/car/export`,
          { cars },
          {
            responseType: "arraybuffer",
            headers: {
              authorization: token,
            },
          },
        )
        .then(async (response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "archive.zip");
          document.body.appendChild(link);
          link.click();
          this.loadingArchive[this.index.toString()] = false;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingArchive[this.index.toString()] = false;
        });
    },
    openQrDialog(data) {
      this.$emit("qr", data);
    },
    openInfoCarCad(car) {
      this.$emit("infocar-cad", {
        brandCode: car.brand.brandCode,
        modelCode: car.model.modelCode,
        versionCode: car.version.versionCode,
      });
    },
    openTransferDialog(car) {
      this.$emit("transfer", { car });
    },
    getSupplyLocale(supply) {
      switch (supply) {
        case "B":
          return this.$t("parts.advanced_search.supply.petrol");
        case "D":
          return this.$t("parts.advanced_search.supply.diesel");
        case "G":
          return this.$t("parts.advanced_search.supply.gas");
        default:
          return supply;
      }
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length
            ? options.groupBy
            : ["date_created"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "date_created", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      this.activeRow = null;
    },
  },
};
</script>

<style scoped>
/* ── Mobile / Desktop visibility toggle ── */
@media (max-width: 959.98px) {
  .vehicles-table-host :deep(.v-table__wrapper),
  .vehicles-table-host :deep(.v-data-table__wrapper),
  .vehicles-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .vehicles-table-host :deep(.global-sticky-hscroll) {
    display: none !important;
  }
  .vehicles-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .vehicles-mobile-list {
    display: none !important;
  }
}

/* ── Mobile card styles ── */
.vehicles-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.vehicles-mobile-card__date {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 0.7rem !important;
  max-width: 55%;
}
.vehicles-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.vehicles-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 8px;
  gap: 8px;
}
.vehicles-mobile-card__media {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.vehicles-mobile-card__image-btn {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.vehicles-mobile-card__img {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  background: #212121;
  display: block;
}
.vehicles-mobile-card__qr-btn {
  min-width: 72px;
  margin-top: 2px;
  padding: 4px 8px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  color: #424242;
  cursor: pointer;
  text-align: center;
}
.vehicles-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.vehicles-mobile-card__info-top {
  padding-right: 88px;
}
.vehicles-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 2px 4px;
}

/* ── Infocar parts availability indicator ── */
.infocar-parts {
  position: relative;
}
.infocar-parts__check {
  position: absolute;
  right: -8px;
  top: 6px;
  background-color: #fff;
  border-radius: 50%;
}
</style>
